import { logException } from "@punchup/sentry";
import { SupabaseClient } from "@supabase/supabase-js";
import { Database } from "../../supabase.types";
import { getUser } from "../../supabase/supabase";
import {
  VideoUploadInsert,
  VideoUploadRow,
  VideoUploadRowWithDetails,
  VideoUploadUpdate,
} from "../../models/video";

export async function insertComedianVideoUpload(
  supabase: SupabaseClient<Database>,
  video: VideoUploadInsert
) {
  const {
    data: { user },
  } = await supabase.auth.getUser();

  if (!user?.id) {
    throw new Error(
      "Supabase client must have an active session to insert a video."
    );
  }

  const { data, status, error } = await supabase
    .from("comedian_video_uploads")
    .insert(video)
    .select("id")
    .single();

  if (error) {
    logException(error, {
      tags: {
        comedianId: video.comedian_id,
        userId: user.id,
      },
    });
    return {
      error: `Oops something went wrong, please try again or contact us.`,
    };
  } else if (status === 201) {
    return { error: null, id: data.id };
  }

  logException(
    `An unspecified error occurred while trying to insert a video for comedian manager`,
    {
      tags: {
        comedianId: video.comedian_id,
        userId: user.id,
      },
    }
  );
  return {
    error: `Oops something went wrong, please try again or contact us.`,
  };
}

/**
 * Resolve a Cloudflare Stream `videoId` to the `comedian_id` that owns it.
 *
 * Deny-by-default ownership lookup used by the cross-posting clip route to block
 * IDOR: a caller may only clip a video that belongs to a comedian they manage.
 * Looks first at `comedian_video_uploads.video_id` (the upload path the wizard
 * uses), then falls back to `specials.video_id`. Returns the owning
 * `comedian_id`, or `null` when no owning row exists — never throws-to-allow, so
 * an unknown/foreign videoId always fails closed.
 */
export async function getComedianIdForVideoId(
  supabase: SupabaseClient<Database>,
  videoId: string
): Promise<string | null> {
  const { data: upload } = await supabase
    .from("comedian_video_uploads")
    .select("comedian_id")
    .eq("video_id", videoId)
    .maybeSingle();

  if (upload?.comedian_id) {
    return upload.comedian_id;
  }

  const { data: special } = await supabase
    .from("specials")
    .select("comedian_id")
    .eq("video_id", videoId)
    .maybeSingle();

  return special?.comedian_id ?? null;
}

export async function getPendingComedianUploads(
  supabase: SupabaseClient<Database>,
  callerIsSystem: boolean,
  comedianId?: string
): Promise<VideoUploadRowWithDetails[] | null> {
  let userId = "";
  if (!callerIsSystem) {
    const user = await getUser(supabase);

    if (!user) {
      throw new Error(
        "Supabase client must have an active session to fetch pending comedian uploads"
      );
    }
    userId = user.id;
  }

  let query = supabase
    .from("comedian_video_uploads")
    .select(
      `
      *,
      comedian:comedians!comedian_video_uploads_comedian_id_fkey!inner (
        display_name
      ),
      collection:collections!comedian_video_uploads_collection_id_fkey (
        name
      )
    `
    )
    .eq("is_pending", true);

  if (comedianId) {
    query = query.eq("comedian_id", comedianId);
  }

  const { status, data, error } = await query.order("created_at", {
    ascending: false,
  });

  if (error) {
    logException(
      `An error occurred while getting pending comedian uploads for user ${userId || "system"}. Error: ${JSON.stringify(error)}`
    );
  } else if (status === 200) {
    return data;
  }
  logException(
    `Issue getting pending comedian uploads for user ${userId || "system"}`
  );
  throw new Error(
    `Issue getting pending comedian uploads for user ${userId || "system"}`
  );
}

export async function getPendingComedianUpload(
  supabase: SupabaseClient<Database>,
  uploadId: string
): Promise<VideoUploadRow | null> {
  const user = await getUser(supabase);

  if (!user) {
    throw new Error(
      "Supabase client must have an active session to fetch pending comedian upload"
    );
  }

  const { status, data, error } = await supabase
    .from("comedian_video_uploads")
    .select(`*`)
    .eq("id", uploadId)
    .single();

  if (error) {
    logException(
      `An error occurred while getting pending comedian upload with id ${uploadId} for user ${user.id}. Error: ${JSON.stringify(error)}`
    );
  } else if (status === 200) {
    return data;
  }
  logException(
    `Issue getting pending comedian upload with id ${uploadId} for user ${user.id}`
  );
  throw new Error(
    `Issue getting pending comedian uploads with id: ${uploadId} for user ${user.id}`
  );
}

export async function updateComedianVideoUpload(
  supabase: SupabaseClient<Database>,
  uploadId: string,
  columnsToUpdate: VideoUploadUpdate,
  callerIsSystem?: boolean
) {
  let userId = "";
  if (!callerIsSystem) {
    const {
      data: { session },
    } = await supabase.auth.getSession();

    if (!session?.user.id) {
      throw new Error(
        "Supabase client must have an active session to update a video upload."
      );
    }
    userId = session.user.id;
  }

  const { count, status, error } = await supabase
    .from("comedian_video_uploads")
    .update(columnsToUpdate, {
      count: "exact",
    })
    .eq("id", uploadId);

  if (error) {
    logException(
      `An error occurred while attempting to update a comedian video upload with ID: ${uploadId} by user with ID: ${userId || "system"}. Error: ${JSON.stringify(error)}`
    );
  } else if (status === 204) {
    if (count === 1) {
      return true;
    } else {
      const error = `Error while attempting to update a comedian video upload with ID: ${uploadId}. Row count was: ${count}.`;
      logException(error);
    }
  }
  logException(
    `An unspecified error occurred while attempting to update a comedian video upload with ID: ${uploadId}, by user ID: ${userId || "system"}`
  );
  return false;
}
